import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getActionDefinitions, actionIcons } from '../../data/actionDefinitions';
import { formatTime } from '../../utils/formatTime';
import { Panel, Badge } from '../../components/design';
import Icon from '../../components/Icon';

const CORE_API = '/api';

export default function ActionsTab({ serverId, server, canEdit, runAction, actionLoading, actionResult }) {
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagResult, setDiagResult] = useState(null);
  const [diagCommand, setDiagCommand] = useState('');

  const { data: tasksData } = useQuery({
    queryKey: ['servers', serverId, 'tasks'],
    queryFn: () => fetch(`${CORE_API}/servers/${serverId}/tasks`).then(res => res.json()),
    refetchInterval: 5000,
  });

  const tasks = tasksData?.tasks || [];

  const runDiagnostic = async (cmd) => {
    setDiagLoading(true);
    setDiagResult(null);
    setDiagCommand(cmd);
    try {
      const res = await fetch(`${CORE_API}/servers/${serverId}/diagnostic`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ command: cmd }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || `HTTP ${res.status}`);
      const data = await res.json();
      setDiagResult(data);
    } catch (e) {
      setDiagResult({ error: e.message });
    }
    setDiagLoading(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Panel title="Server Actions" actions={actionLoading && <span className="muted" style={{ fontSize: 12 }}>Executing...</span>}>
        <div style={{ marginBottom: 20 }}>
          <div className="muted" style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>Safe Actions</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
            {getActionDefinitions(server).filter(d => d.group === 'safe').map(def => (
              <button key={def.action} onClick={() => runAction(def.action)} disabled={actionLoading}
                style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: 16, borderRadius: 12, background: 'var(--glass)', border: '1px solid var(--glass-border)', textAlign: 'left', cursor: 'pointer', opacity: actionLoading ? 0.5 : 1 }}>
                <span style={{ marginTop: 2, color: 'var(--success)', flexShrink: 0 }}>{actionIcons[def.icon]}</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--success)' }}>{def.label}</div>
                  <div className="muted" style={{ fontSize: 12, marginTop: 2, lineHeight: 1.5 }}>{def.description}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="muted" style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12, color: 'var(--danger)' }}>Dangerous Actions</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
            {getActionDefinitions(server).filter(d => d.group === 'danger').map(def => (
              <button key={def.action} onClick={() => runAction(def.action)} disabled={actionLoading}
                style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: 16, borderRadius: 12, background: 'var(--glass)', border: '1px solid var(--glass-border)', textAlign: 'left', cursor: 'pointer', opacity: actionLoading ? 0.5 : 1 }}>
                <span style={{ marginTop: 2, color: 'var(--danger)', flexShrink: 0 }}>{actionIcons[def.icon]}</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--danger)' }}>{def.label}</div>
                  <div className="muted" style={{ fontSize: 12, marginTop: 2, lineHeight: 1.5 }}>{def.description}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </Panel>

      {actionResult && (
        <Panel title={actionResult.error ? 'Error' : 'Result'}>
          <pre className="mono" style={{ fontSize: 12, color: actionResult.error ? 'var(--danger)' : 'var(--text-2)', whiteSpace: 'pre-wrap', maxHeight: 288, overflow: 'auto', margin: 0 }}>
            {actionResult.output || actionResult.detail || actionResult.message || JSON.stringify(actionResult, null, 2)}
          </pre>
        </Panel>
      )}

      <Panel title="Diagnostic Commands" actions={<>{diagCommand && <span className="muted" style={{ fontSize: 12 }}>Running: {diagCommand}</span>}{diagLoading && <Icon name="refresh-cw" size={14} />}</>}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 }}>
          {[
            { label: 'CPU Info', cmd: 'cpu_info', desc: '/proc/cpuinfo + lscpu' },
            { label: 'Memory Info', cmd: 'memory_info', desc: 'free -h + /proc/meminfo' },
            { label: 'Disk Usage', cmd: 'disk_usage', desc: 'df -h + lsblk' },
            { label: 'Network Stats', cmd: 'network_stats', desc: 'ip a + ss -tlnp' },
            { label: 'Processes', cmd: 'processes', desc: 'ps aux --sort=-%mem' },
            { label: 'System Logs', cmd: 'system_logs', desc: 'journalctl -n 100' },
            { label: 'Uptime', cmd: 'uptime', desc: 'uptime + who -b' },
            { label: 'Load Average', cmd: 'load_average', desc: 'cat /proc/loadavg' },
          ].map(d => (
            <button key={d.cmd} onClick={() => runDiagnostic(d.cmd)} disabled={diagLoading}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: 12, borderRadius: 12, background: 'var(--glass)', border: '1px solid var(--glass-border)', textAlign: 'left', cursor: 'pointer', opacity: diagLoading ? 0.5 : 1 }}>
              <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-1)' }}>{d.label}</span>
              <span className="mono muted" style={{ fontSize: 10, marginTop: 2 }}>{d.desc}</span>
            </button>
          ))}
        </div>
        {diagResult && (
          <div style={{ marginTop: 16, borderRadius: 12, border: '1px solid var(--glass-border)', overflow: 'hidden' }}>
            <div className="flex between center" style={{ padding: '8px 14px', background: 'var(--glass)' }}>
              <span className="muted" style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{diagCommand || 'Output'}</span>
              <button onClick={() => setDiagResult(null)} style={{ cursor: 'pointer', background: 'none', border: 'none', color: 'var(--text-3)' }}><Icon name="x" size={15} /></button>
            </div>
            <div style={{ padding: 16, maxHeight: 320, overflow: 'auto' }}>
              {diagResult.error ? (
                <div style={{ fontSize: 14, color: 'var(--danger)' }}>{diagResult.error}</div>
              ) : diagResult.stdout ? (
                <div>
                  <div className="flex items-center gap-2" style={{ marginBottom: 8 }}>
                    <span className="muted" style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase' }}>Command: {diagCommand}</span>
                    <Badge tone={diagResult.exit_code === 0 ? 'green' : 'red'}>Exit: {diagResult.exit_code}</Badge>
                  </div>
                  <pre className="mono" style={{ whiteSpace: 'pre-wrap', fontSize: 12, color: 'var(--text-2)', margin: 0 }}>{diagResult.stdout}</pre>
                  {diagResult.stderr && <pre className="mono" style={{ whiteSpace: 'pre-wrap', fontSize: 12, color: 'var(--danger)', marginTop: 8, marginBottom: 0 }}>{diagResult.stderr}</pre>}
                </div>
              ) : (
                <pre className="mono" style={{ whiteSpace: 'pre-wrap', fontSize: 12, color: 'var(--text-2)', margin: 0 }}>{JSON.stringify(diagResult, null, 2)}</pre>
              )}
            </div>
          </div>
        )}
      </Panel>

      {tasks.length > 0 && (
        <Panel title="Recent Tasks">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {tasks.slice(0, 10).map(task => (
              <div key={task.id} className="flex between center" style={{ padding: 12, borderRadius: 12, background: 'var(--glass)', border: '1px solid var(--glass-border)' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-1)' }}>{task.type}</div>
                  <div className="muted" style={{ fontSize: 12 }}>{formatTime(task.created_at)}</div>
                </div>
                <Badge tone={task.status === 'completed' ? 'green' : task.status === 'failed' ? 'red' : 'amber'}>{task.status}</Badge>
              </div>
            ))}
          </div>
        </Panel>
      )}
    </div>
  );
}
